import { FormProvider, useFieldArray } from 'react-hook-form';
import { updateFormData, updateFormDataBack } from '~/store/identification-form/slices';
import { IdentificationFormStepNames, IdentificationFormSteps } from '../../types';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { useTaxResidencyAdditionalInformationForm } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/hooks/useTaxResidencyAdditionalInformationForm.ts';
import { TTaxResidencyAdditionalInformation } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/types.ts';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { OrganizationSelfAssessmentDocumentForm } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/OrganizationSelfAssessmentDocumentForm/OrganizationSelfAssessmentDocumentForm.tsx';
import { styles } from '../../styles.ts';
import { updateForm } from '~/store/identification-form/thunks/thunks.ts';
import { TIdentificationForm } from '~/store/identification-form/types.ts';
import { useSelector } from 'react-redux';
import {
  selectApplicationId,
  selectCompletedForms,
} from '~/store/identification-form/selectors.ts';
import { ControllingPersonSelfAssessmentDocumentForm } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/ControllingPersonSelfAssessmentDocumentForm/ControllingPersonSelfAssessmentDocumentForm.tsx';
import {
  EMPTY_CONTROLLING_PERSONS,
  showBeneficiaryOwners,
  testDataTaxResidencyAdditionalInformation,
} from '~/modules/identification/forms/TaxResidencyAdditionalInformation/constants.ts';
import { commonStyles } from '~/theme/styles/commonStyles.ts';
import { getTaxResidenciesValues } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/utils.ts';
import { useBeneficiaryOwners } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/hooks/useBeneficiaryOwners.ts';
import { onInvalidForm } from '~/helpers/utils.ts';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { SetDirtyModalValues } from '../../components/SetDirtyModalValues.tsx';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext.tsx';

interface ITaxResidencyAdditionalInformationProps {
  onNextStep: () => void;
}

export const TaxResidencyAdditionalInformation = ({
  onNextStep,
}: ITaxResidencyAdditionalInformationProps) => {
  const dispatch = useAppDispatch();
  const appId = useSelector(selectApplicationId);
  const { beneficiaryOwners } = useBeneficiaryOwners();
  const { isTestEnvironmentEnabled } = useFeatureFlags();
  const { isTaxResidencyAdditionalInformationCompleted } = useSelector(selectCompletedForms);

  const handleFillTestData = () =>
    form.reset(testDataTaxResidencyAdditionalInformation, { keepDefaultValues: true });

  const form = useTaxResidencyAdditionalInformationForm();
  const { isDirty, isSubmitting } = form.formState;
  const { control, watch } = form;
  const { fields: fieldsControllingPersons, append: appendControllingPersons } = useFieldArray({
    control,
    name: 'controllingPersons',
  });
  const selectedOption = watch('accountHolderStatus.selectedOption');

  const beneficiaryOwnersCards =
    beneficiaryOwners.length > 0 && showBeneficiaryOwners.includes(selectedOption!);

  if (!fieldsControllingPersons.length) appendControllingPersons(EMPTY_CONTROLLING_PERSONS);

  const onSubmit =
    (isDirty: boolean, disableGoNext?: boolean) =>
    async (values: TTaxResidencyAdditionalInformation) => {
      if (isDirty || !isTaxResidencyAdditionalInformationCompleted) {
        try {
          const taxResidencyAdditionalInformationValue = getTaxResidenciesValues(values);

          const data = {
            taxResidencyAdditionalInformation: taxResidencyAdditionalInformationValue,
            applicationId: appId as number,
          };
          await dispatch(
            updateForm({
              data,
              reportId: IdentificationFormSteps.taxResidencyAdditionalInformation,
            })
          );
          const formData = {
            field:
              IdentificationFormStepNames.taxResidencyAdditionalInformation as keyof TIdentificationForm,
            data: taxResidencyAdditionalInformationValue,
          };
          dispatch(updateFormData(formData));
          dispatch(updateFormDataBack(formData));
          !disableGoNext && onNextStep();
        } catch (error) {
          console.error(error);
        }
      } else {
        !disableGoNext && onNextStep();
      }
    };

  return (
    <FormProvider {...form}>
      {isTestEnvironmentEnabled && (
        <Box sx={styles.testEnvironmentButton}>
          <Button variant={'outlined'} onClick={handleFillTestData}>
            Заповнити даними
          </Button>
        </Box>
      )}
      <Box component="form" onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}>
        <Typography variant="h5" sx={styles.titleForm}>
          Форма Документа Самостійної Оцінки Організації (CRS-E)
        </Typography>
        <OrganizationSelfAssessmentDocumentForm />
        {beneficiaryOwnersCards && (
          <Box sx={{ ...commonStyles.columnFlexContainer, gap: 3, mt: 3 }}>
            <Divider sx={styles.divider} />
            <Typography variant="h5" sx={styles.titleForm}>
              Форма Документа Самостійної Оцінки Контролюючої Особи (CRS-CP)
            </Typography>
            {beneficiaryOwners.map((person, index) => (
              <ControllingPersonSelfAssessmentDocumentForm key={person.id} index={index} />
            ))}
          </Box>
        )}
        <Grid size={12} sx={{ justifyContent: 'center', display: 'flex', paddingTop: 2 }}>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            Продовжити
          </Button>
        </Grid>
        <SetDirtyModalValues
          saveValues={form.handleSubmit(onSubmit(isDirty, true), onInvalidForm)}
        />
      </Box>
      {isSubmitting && <FullPageLoader />}
    </FormProvider>
  );
};
